import {
  ActionIcon,
  Anchor,
  AspectRatio,
  Center,
  Group,
  Image,
  Paper,
  Skeleton,
  Stack,
  Title,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import classes from "../../elements/ImageGallery/ImageGallery.module.css";

import carburateLogo from "@/assets/illustrations/carburate-logo-small.svg";
import { DataGrid, LoadedImage, getChangeType } from "@/components/elements";
import { IconLink } from "@/components/icons";
import { env } from "@/config/env";
import { paths } from "@/config/paths";
import { defaultCurrency } from "@/lib/currency";
import { useCar } from "@/services/cars/get-car";
import { useCarImages } from "@/services/cars/get-car-images";
import { useMarketCar } from "@/services/market/get-market-car";
import { useMarketCarImages } from "@/services/market/get-market-car-images";
import { Currency } from "@/types/currency";
import { formatCurrency, formatPercentage } from "@/utils/formatEuro";
import { getValuePool } from "@/utils/getValuePool";

import type { LabelValueProps } from "@/components/elements";
import type { FC } from "react";

export type CarComparePreviewProps = {
  carId: string;
};

function getColorFromValue(value = 0) {
  if (value < 0) {
    return "danger";
  }
  return undefined;
}

function getConditionAdjustedValuation(marketData: any, condition?: string) {
  if (!marketData) return undefined;
  const byCondition = marketData?.market_prices?.valuation_by_condition;
  if (condition && byCondition && condition in byCondition) {
    return byCondition[condition as keyof typeof byCondition];
  }
  return marketData?.current_valuation;
}

export const CarComparePreview: FC<CarComparePreviewProps> = ({ carId }) => {
  const { t } = useTranslation();
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });
  // Data
  const { data, isLoading } = useCar({ carId });
  const { data: marketData, isLoading: marketDataIsLoading } = useMarketCar({
    carId: data?.taxonomy_id,
  });
  const carLink =
    (data?.garages || []).length > 0
      ? paths.app.garage.car.getHref(data!.garages[0], carId)
      : paths.app.formerCars.car.getHref(carId);

  // Image
  const { data: gallery, isLoading: galleryIsLoading } = useCarImages({ carId });
  const { data: marketGallery, isLoading: marketGalleryIsLoading } = useMarketCarImages({
    carId: data?.taxonomy_id,
  });
  const image = gallery?.results?.[0] || marketGallery?.results?.[0];

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const intro: LabelValueProps[] = [
    {
      label: t("compare.labels.make"),
      value: marketData?.make_id ? (
        <Anchor
          component={Link}
          to={paths.app.markets.makeDetails.getHref(marketData?.make_id)}
          size="lg"
        >
          {marketData.make_name || data?.make}
        </Anchor>
      ) : (
        "-"
      ),
    },
    {
      label: t("compare.labels.model"),
      value: data?.model || "-",
    },
    {
      label: t("compare.labels.color"),
      value: data?.color || "-",
    },
    {
      label: t("compare.labels.production_year"),
      value: data?.production_year || "-",
    },
  ];
  const values: LabelValueProps[] = [
    {
      label: t("compare.labels.acquisition_cost"),
      value: formatCurrency(data?.purchase_price, currency.code),
      c: getColorFromValue(data?.purchase_price),
    },
    // {
    //   label: t("compare.labels.total_ownership_cost"),
    //   value: formatCurrency(data?.ownership_cost, currency.code),
    // },
    {
      label: t("compare.labels.market_value"),
      value: formatCurrency(
        getConditionAdjustedValuation(marketData, data?.condition),
        currency.code
      ),
      c: getColorFromValue(getConditionAdjustedValuation(marketData, data?.condition)),
    },
    {
      label: t("compare.labels.lifetime_gain_loss"),
      value:
        data?.lifetime_gain_lost !== undefined
          ? formatCurrency(Math.abs(data.lifetime_gain_lost), currency.code)
          : "-",
      c: getColorFromValue(data?.lifetime_gain_lost),
      withChange: getChangeType(data?.lifetime_gain_lost),
      infoIcon: t("tooltips.lifetimeGainLoss"),
    },
    {
      label: t("compare.labels.roi"),
      value: formatPercentage(data?.roi),
      c: getColorFromValue(data?.roi),
      infoIcon: t("tooltips.roi"),
    },
    {
      label: t("compare.labels.total_gain_loss"),
      /*       value: formatCurrency(data?.lifetime_gain_lost, currency.code), */
      value:
        data?.lifetime_gain_lost !== undefined
          ? formatCurrency(Math.abs(data.annual_gain_lost), currency.code)
          : "-",
      c: getColorFromValue(data?.lifetime_gain_lost),
      withChange: getChangeType(data?.lifetime_gain_lost),
    },
    {
      label: t("compare.labels.annual_gain_loss"),
      value:
        data?.annual_gain_lost !== undefined
          ? formatCurrency(Math.abs(data.annual_gain_lost), currency.code)
          : "-",
      c: getColorFromValue(data?.annual_gain_lost),
      withChange: getChangeType(data?.annual_gain_lost),
      infoIcon: t("tooltips.maxDrawdown"),
    },
  ];
  const market: LabelValueProps[] = [
    {
      label: t("compare.labels.lowest_hammer_price_3y"),
      value: formatCurrency(marketData?.market_prices?.hammer_price_3y?.lowest, currency.code),
    },
    {
      label: t("compare.labels.average_hammer_price_3y"),
      value: formatCurrency(marketData?.market_prices?.hammer_price_3y?.average, currency.code),
    },
    {
      label: t("compare.labels.highest_hammer_price_3y"),
      value: formatCurrency(marketData?.market_prices?.hammer_price_3y?.highest, currency.code),
    },
    {
      label: t("compare.labels.value_pool"),
      value: getValuePool(data?.current_value),
    },
  ];

  const bgColor = isDark ? "#323232" : "#d9d9d9";

  return (
    <Skeleton visible={isLoading || marketDataIsLoading} flex={1}>
      <Stack>
        <Group justify="space-between">
          <Title order={2}>
            {data?.make} - {data?.model}
            {data?.nickname && ` (${data.nickname})`}
          </Title>
          <Tooltip label={t("compare.tooltip")} events={{ hover: true, focus: true, touch: true }}>
            <ActionIcon component={Link} to={carLink} variant="light">
              <IconLink />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Skeleton visible={galleryIsLoading || marketGalleryIsLoading}>
          <AspectRatio ratio={16 / 9}>
            <Paper h={300} shadow="none">
              {image && image.path.includes(env.API_URL) ? (
                <LoadedImage image={image} />
              ) : image ? (
                <Image
                  src={image.path}
                  alt={image.name}
                  className={classes.mainImage}
                  radius="md"
                  fit="cover"
                />
              ) : (
                /* Car Image Placeholder */
                <Center bg={bgColor} bdrs="md" h="100%">
                  <Image src={carburateLogo} w="auto" height="50%" alt="Carburate" />
                </Center>
              )}
            </Paper>
          </AspectRatio>
        </Skeleton>
        <DataGrid cols={1} items={intro} gridProps={{ verticalSpacing: "xs" }} horizontal />
        <DataGrid
          cols={1}
          items={values}
          gridProps={{ verticalSpacing: "xs" }}
          horizontal
          title={<Title order={3}>{t("compare.titles.value_costs")}</Title>}
        />
        <DataGrid
          cols={1}
          items={market}
          gridProps={{ verticalSpacing: "xs" }}
          horizontal
          title={<Title order={3}>{t("compare.titles.market_headlines")}</Title>}
        />
      </Stack>
    </Skeleton>
  );
};
